import moment from "moment-timezone";
import TimeUtils from "./time";

export interface StreakState {
  streak: number;
  streakInDanger: boolean;
  streakWasInDanger: boolean;
  timestamp: number; // For jitter prevention
  userId?: string; // To ensure jitter prevention is per-user
}

export interface UserStreakData {
  streak?: number;
  streakInDanger?: boolean;
}

export default class Streak {
  /**
   * Calculate the real-time streak state based on the last tugged date and current streak.
   * This logic is the SOURCE OF TRUTH for determining if a streak is active, broken, or in danger.
   *
   * @param lastTuggedDate - The date the user last tugged
   * @param currentStreak - The streak count stored in the DB
   * @param timezone - The user's timezone
   * @returns StreakState object
   */
  static calculateRealtimeState(
    lastTuggedDate: moment.Moment | string | null | undefined,
    currentStreak: number,
    timezone: string | null = null,
    userId: string | null = null
  ): StreakState {
    if (!lastTuggedDate || currentStreak <= 0) {
      return {
        streak: 0,
        streakInDanger: false,
        streakWasInDanger: false,
        timestamp: Date.now(),
        userId: userId || undefined,
      };
    }

    const tz = timezone || TimeUtils.getTimezone();
    // Current time in User's timezone
    const now = moment().tz(tz);
    const today = now.clone().startOf("day");
    const yesterday = today.clone().subtract(1, "day");

    // Resolve lastTugged to a Moment object in the User's timezone
    let lastTugged = moment(lastTuggedDate);
    if (lastTugged.isValid()) {
      lastTugged = lastTugged.tz(tz);
    } else {
      // If invalid, return valid empty state to avoid crash
      return {
        streak: 0,
        streakInDanger: false,
        streakWasInDanger: false,
        timestamp: Date.now(),
        userId: userId || undefined,
      };
    }

    const lastTuggedDay = lastTugged.clone().startOf("day");

    let streak = currentStreak;
    let streakInDanger = false;
    let streakWasInDanger = false;

    if (lastTuggedDay.isSame(today, "day")) {
      // Tugged today: Streak is safe
      streakInDanger = false;
    } else if (lastTuggedDay.isSame(yesterday, "day")) {
      // Tugged yesterday: Streak is in danger
      streakInDanger = true;
    } else if (lastTuggedDay.isBefore(yesterday, "day")) {
      // Tugged before yesterday: Streak is broken
      streak = 0;
      streakInDanger = false;
    }
    // Note: If lastTuggedDay is in the future (e.g. timezone diff), we treat it as safe (today) usually,
    // or we could say it's safe.
    // For now, if it's after today, it falls through.
    // If lastTugged is future, let's assume safe.
    if (lastTuggedDay.isAfter(today, "day")) {
      streakInDanger = false;
    }

    return {
      streak,
      streakInDanger,
      streakWasInDanger,
      timestamp: Date.now(),
      userId: userId || undefined,
    };
  }

  /**
   * Predict the next streak state after a successful tug session happens NOW.
   * This is for OPTIMISTIC UI updates before the backend confirms.
   *
   * @param lastTuggedDate - The PREVIOUS last tugged date (before this session)
   * @param currentStreak - The PREVIOUS streak count
   * @param timezone - The user's timezone
   * @returns StreakState
   */
  static predictNextState(
    lastTuggedDate: moment.Moment | string | null | undefined,
    currentStreak: number,
    timezone: string | null = null
  ): StreakState {
    const tz = timezone || TimeUtils.getTimezone();
    const now = moment().tz(tz);
    const today = now.clone().startOf("day");
    const yesterday = today.clone().subtract(1, "day");

    // Default: New user or no history
    if (!lastTuggedDate) {
      return {
        streak: 1,
        streakInDanger: false,
        streakWasInDanger: false,
        timestamp: Date.now(),
      };
    }

    let lastTugged = moment(lastTuggedDate);
    if (lastTugged.isValid()) {
      lastTugged = lastTugged.tz(tz);
    } else {
      return {
        streak: 1,
        streakInDanger: false,
        streakWasInDanger: false,
        timestamp: Date.now(),
      };
    }

    const lastTuggedDay = lastTugged.clone().startOf("day");

    // Case 1: Tugged Today (Already)
    if (lastTuggedDay.isSame(today, "day")) {
      return {
        streak: currentStreak > 0 ? currentStreak : 1,
        streakInDanger: false,
        streakWasInDanger: false,
        timestamp: Date.now(),
      };
    }

    // Case 2: Tugged Yesterday (In Danger -> Safe + Increment)
    if (lastTuggedDay.isSame(yesterday, "day")) {
      return {
        streak: currentStreak + 1,
        streakInDanger: false,
        streakWasInDanger: true, // It WAS in danger
        timestamp: Date.now(),
      };
    }

    // Case 3: Tugged Before Yesterday (Broken -> New Streak) or Future
    return {
      streak: 1,
      streakInDanger: false,
      streakWasInDanger: false,
      timestamp: Date.now(),
    };
  }

  /**
   * Backwards compatible method for existing components.
   * Calculates state from user object but prefers robust date logic if dates are available.
   */
  static extractState(
    userObj: any, // Using any to be flexible with User model
    previousState?: StreakState
  ): StreakState {
    if (!userObj) {
      return {
        streak: 0,
        streakInDanger: false,
        streakWasInDanger: previousState?.streakWasInDanger || false,
        timestamp: Date.now(),
      };
    }

    let newState: StreakState;

    // If generic object with proper dates
    if (userObj.dates && userObj.dates.lastTugged) {
      const calculated = Streak.calculateRealtimeState(
        userObj.dates.lastTugged,
        userObj.streak || userObj._streak || 0,
        userObj.timezone,
        userObj.uid || userObj.id
      );

      newState = {
        ...calculated,
        streakWasInDanger: previousState?.streakWasInDanger || false,
      };
    } else {
      // Fallback for objects without dates
      newState = {
        streak: userObj.streak || 0,
        streakInDanger: userObj.streakInDanger || false,
        streakWasInDanger: previousState?.streakWasInDanger || false,
        timestamp: Date.now(),
        userId: userObj.uid || userObj.id || undefined,
      };
    }

    // Jitter Prevention Logic
    // If previous state had a HIGHER streak, it might be an optimistic UI update
    // that the backend hasn't caught up with yet.
    // We only apply this if the previous state is very recent (within 10 seconds).
    // CRITICAL: We only apply this if the userId matches!
    if (
      previousState &&
      previousState.userId === newState.userId &&
      previousState.streak > newState.streak &&
      Date.now() - previousState.timestamp < 2000
    ) {
      // Keep the higher streak for a moment to avoid UI flickering
      return {
        ...previousState,
        streakWasInDanger:
          newState.streakWasInDanger || previousState.streakWasInDanger,
      };
    }

    return newState;
  }

  /**
   * Get streak color based on state
   * @param streak - Streak number
   * @param streakInDanger - Whether streak is in danger
   * @returns Color hex string
   */
  static getColor(streak: number, streakInDanger: boolean): string {
    if (streak > 0) {
      return streakInDanger ? "#ffab00" : "#00c853";
    }
    return "#78909c";
  }

  /**
   * Format streak display text
   * @param streak - Streak number
   * @returns Formatted string (e.g., "5d")
   */
  static formatDisplay(streak: number): string {
    return `${Math.max(streak, 0)}d`;
  }

  /**
   * Check if streak should show celebration
   */
  static shouldCelebrate(
    streak: number,
    streakInDanger: boolean,
    streakWasInDanger: boolean,
    previousStreak?: number
  ): boolean {
    // Celebrate if streak was in danger and is now safe
    if (streak > 0 && streakWasInDanger && !streakInDanger) {
      return true;
    }

    // Also celebrate if streak has incremented (new day, streak continues)
    if (
      previousStreak !== undefined &&
      streak > previousStreak &&
      streak > 0 &&
      !streakInDanger
    ) {
      return true;
    }

    return false;
  }
}
